"use client";

import { TabList } from "@ui/components/core/TabList";
import { useId, useState } from "react";
import { Tabs, TabPanel, type Key } from "react-aria-components";
import * as m from "motion/react-m";
import { AnimatePresence, type MotionNodeAnimationOptions } from "motion/react";
import type { Demo, DemoGroup } from "./BlockInteractCreateOmnibox";
import { captureEvent } from "@/app/_analytics/analytics";
import { EventName } from "@mw/shared/analyticsEventNames";
import { clsx } from "clsx";
import { LinkTracked } from "@ui/components/core/LinkTracked";
import { HairlineCardBorder } from "@ui/components/core/HairlineCardBorder";
import { getLinkProps } from "@utilities/payload";
import { pxToRem } from "@ui/lib/utils";

export function BlockInteractCreateOmniboxClient({ demoGroups }: { demoGroups: DemoGroup[] }) {
  const grouped = demoGroups.length > 1;
  const totalDemoCount = demoGroups.reduce((acc, group) => acc + group.demos.length, 0);

  return (
    <div
      className={clsx(
        "tw-relative tw-isolate tw-w-full",
        grouped ? "tw-mt-20 min-[53rem]:tw-mt-0" : "tw-mt-7 sm:tw-mt-0",
      )}
    >
      {/* Overlay to hide the section side lines */}
      <div
        className='tw-absolute -tw-z-10 tw-top-0 -tw-inset-x-px tw-h-8'
        style={{ backgroundColor: "var(--marker-background-color)" }}
      />
      <div
        className='tw-absolute -tw-z-10 tw-top-0 -tw-inset-x-px tw-h-8'
        style={{ backgroundColor: "var(--marker-background-color)" }}
      />
      <div
        className={clsx(
          "tw-relative -tw-mx-px tw-isolate tw-bg-cream tw-rounded-3xl tw-h-[37rem] sm:tw-h-[37rem] tw-grid tw-gap-x-6 [--full-bleed:theme(spacing.4)] sm:[--full-bleed:theme(spacing.8)]",
          totalDemoCount > 1 && "min-[53rem]:tw-pt-6",
          grouped
            ? "tw-grid-cols-[1fr_auto] min-[53rem]:tw-grid-cols-[auto_1fr] tw-grid-rows-[auto_1fr_auto_auto] sm:tw-grid-rows-[auto_1fr_auto] tw-px-4 sm:tw-px-6"
            : "tw-grid-cols-[1fr_auto] min-[53rem]:tw-grid-cols-[auto_1fr] tw-grid-rows-[auto_1fr_auto] tw-px-4 sm:tw-px-8",
          // Used to track omnibox visibility, see `VisibilityProvider.tsx`
          "omnibox-wrapper",
        )}
      >
        <HairlineCardBorder roundedClassName='tw-rounded-3xl' />
        <DemoGroups demoGroups={demoGroups} />
      </div>
    </div>
  );
}

function DemoGroups({ demoGroups }: { demoGroups: DemoGroup[] }) {
  const [selectedKey, setSelectedKey] = useState<Key>(demoGroups[0].id);

  if (demoGroups.length === 1) {
    return <DemoGroup demoGroup={demoGroups[0]} grouped={false} />;
  }

  return (
    <Tabs
      selectedKey={selectedKey}
      onSelectionChange={key => {
        if (key !== selectedKey) {
          captureEvent(EventName.website_clicked_omnibox_tab, { tab: key });
        }
        setSelectedKey(key);
      }}
      className='tw-contents'
    >
      <div className='tw-z-50 tw-absolute tw-left-0 tw-w-full tw-bottom-full tw-mb-2 min-[53rem]:tw-mb-0 min-[53rem]:tw-relative min-[53rem]:tw-inset-auto min-[53rem]:tw-col-start-1 min-[53rem]:tw-row-start-1 tw-rounded-full'>
        <div
          className='tw-contents min-[53rem]:tw-hidden'
          style={{ "--full-bleed": pxToRem(20) } as React.CSSProperties}
        >
          <TabList items={demoGroups} surface='cream' size='md' background centered equalWidth fullWidth />
        </div>
        <div className='tw-hidden min-[53rem]:tw-contents'>
          <TabList items={demoGroups} surface='cream' size='md' centered fullWidth />
        </div>
      </div>
      <AnimatePresence mode='wait' initial={false}>
        {demoGroups.map(demoGroup => {
          return selectedKey === demoGroup.id ? (
            <TabPanel key={demoGroup.id} id={demoGroup.id} shouldForceMount className='tw-contents'>
              <m.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className='tw-grid tw-col-start-1 tw-row-start-1 tw-col-span-full tw-row-span-full tw-grid-cols-[subgrid] tw-grid-rows-[subgrid]'
              >
                <DemoGroup demoGroup={demoGroup} grouped={true} />
              </m.div>
            </TabPanel>
          ) : null;
        })}
      </AnimatePresence>
    </Tabs>
  );
}

function DemoGroup({ demoGroup, grouped }: { demoGroup: DemoGroup; grouped: boolean }) {
  const groupId = useId();
  const [selectedKey, setSelectedKey] = useState<Key>(demoGroup.demos[0].id);

  if (demoGroup.demos.length === 1) {
    return <Demo groupId={groupId} demo={demoGroup.demos[0]} grouped={grouped} onlyDemoInGroup />;
  }

  return (
    <Tabs
      selectedKey={selectedKey}
      onSelectionChange={key => {
        if (key !== selectedKey) {
          captureEvent(EventName.website_clicked_omnibox_tab, { tab: key });
        }
        setSelectedKey(key);
      }}
      className='tw-contents'
    >
      <div
        className={clsx(
          "tw-pt-4 sm:tw-pt-6 min-[53rem]:tw-pt-0",
          grouped
            ? "min-[53rem]:tw-pb-6 tw-col-start-1 tw-col-span-full tw-row-start-1 min-[53rem]:tw-row-start-3 tw-w-full min-[53rem]:tw-w-fit lg:tw-w-auto"
            : "tw-col-span-full",
        )}
      >
        <TabList items={demoGroup.demos} size={grouped ? "sm" : "md"} surface='cream' showRing={false} centered />
      </div>
      <AnimatePresence mode='wait' initial={false}>
        {demoGroup.demos.map(demo => {
          return selectedKey === demo.id ? (
            <TabPanel
              key={demo.id}
              id={demo.id}
              aria-describedby={`${groupId}-${demo.id}-description`}
              shouldForceMount
              className='tw-contents'
            >
              <Demo groupId={groupId} demo={demo} grouped={grouped} />
            </TabPanel>
          ) : null;
        })}
      </AnimatePresence>
    </Tabs>
  );
}

function Demo({
  groupId,
  demo,
  grouped,
  onlyDemoInGroup = false,
}: {
  groupId: string;
  demo: Demo;
  grouped: boolean;
  onlyDemoInGroup?: boolean;
}) {
  const linkProps = getLinkProps(demo.link);
  const animation: MotionNodeAnimationOptions = onlyDemoInGroup
    ? {}
    : {
        initial: { opacity: 0 },
        animate: { opacity: 1 },
        exit: { opacity: 0 },
      };

  return (
    <>
      <m.div
        {...animation}
        className={clsx(
          "tw-px-4 sm:tw-px-0",
          grouped
            ? "tw-col-span-full sm:tw-col-start-1 sm:tw-col-span-1 tw-pb-5 sm:tw-pb-7 min-[53rem]:tw-pb-0 min-[53rem]:tw-pt-[0.328125rem] min-[53rem]:tw-pr-1 tw-text-center sm:tw-text-left min-[53rem]:tw-text-right tw-row-start-3 min-[53rem]:tw-row-auto min-[53rem]:tw-col-start-2"
            : "tw-col-span-full sm:tw-col-auto tw-pb-5 tw-col-start-1 tw-row-start-3 sm:tw-pb-8 tw-text-center sm:tw-text-left",
        )}
      >
        <p className='tw-type-base tw-text-black tw-text-pretty tw-hidden sm:tw-block' aria-hidden>
          {demo.title}
        </p>
        <p
          id={`${groupId}-${demo.id}-description`}
          className='tw-type-xs sm:tw-text-pretty tw-text-gray-500 sm:tw-mt-3 tw-text-balance'
        >
          {demo.description}
        </p>
      </m.div>
      <m.div
        className={clsx(
          "tw-col-span-full tw-col-start-1 tw-row-start-2",
          onlyDemoInGroup && grouped && "min-[53rem]:tw-pb-16",
        )}
        {...animation}
      >
        {demo.component}
      </m.div>
      {linkProps && (
        <div
          className={clsx(
            "tw-col-span-full tw-row-start-4 sm:tw-row-start-3 tw-relative tw-isolate min-[53rem]:tw-absolute sm:tw-col-auto",
            grouped
              ? "tw-pb-5 sm:tw-pb-0 tw-flex tw-justify-center sm:-tw-my-0.75 min-[53rem]:tw-right-7 min-[53rem]:tw-bottom-6 min-[53rem]:tw-my-0"
              : "tw-pb-5 sm:tw-pb-0 tw-flex tw-justify-center sm:-tw-my-0.75 min-[53rem]:tw-right-8 min-[53rem]:tw-bottom-8",
          )}
        >
          <LinkTracked
            {...linkProps}
            eventName={EventName.mw_below_omnibox_cta}
            eventProperties={{ tab: demo.name }}
            asButton
            buttonVariants={{ size: "md", variant: demo.link?.appearance === "primary" ? "primary" : "elevated" }}
          />
        </div>
      )}
    </>
  );
}
